import type { AuthenticatedUser } from '../../types/domain'
import { cacheDetail } from './cache'
import { classifySyncError, retryDelayMs } from './conflict'
import { backendCanBeReached } from './connectivity'
import { executeRemoteOperation } from './remote'
import { hasCompletedDependencies, listOperations, removeOperation, updateOperation } from './outbox'
import type { OutboxOperation, SyncResult } from './types'

const MAX_ATTEMPTS = 5
let activeSync: Promise<SyncResult> | null = null

export async function submitOperation(operation: OutboxOperation, user: AuthenticatedUser): Promise<{ queued: boolean; detail: Awaited<ReturnType<typeof executeRemoteOperation>> }> {
  if (!(await backendCanBeReached())) return { queued: true, detail: null }
  const working = { ...operation, status: 'PROCESSING' as const, attempts: operation.attempts + 1, lastAttemptAt: new Date().toISOString() }
  await updateOperation(working)
  try {
    const detail = await executeRemoteOperation(working)
    if (detail) await cacheDetail(user.userId, detail)
    await removeOperation(working.operationId)
    return { queued: false, detail }
  } catch (error) {
    const kind = classifySyncError(error)
    if (kind === 'AUTH') {
      await updateOperation({ ...working, status: 'PENDING', lastError: errorMessage(error), nextAttemptAt: nextAttempt(working.attempts) })
      throw error
    }
    if (kind === 'TRANSIENT') {
      await updateOperation({ ...working, status: 'PENDING', lastError: errorMessage(error), nextAttemptAt: nextAttempt(working.attempts) })
      return { queued: true, detail: null }
    }
    await updateOperation({ ...working, status: 'NEEDS_ATTENTION', lastError: errorMessage(error), nextAttemptAt: null })
    throw error
  }
}

export function processOutbox(user: AuthenticatedUser): Promise<SyncResult> {
  if (activeSync) return activeSync
  activeSync = processSequentially(user).finally(() => { activeSync = null })
  return activeSync
}

async function processSequentially(user: AuthenticatedUser): Promise<SyncResult> {
  if (!(await backendCanBeReached())) return summary(user.userId, 0)
  let processed = 0
  const operations = await listOperations(user.userId, ['PENDING', 'PROCESSING'])
  for (const operation of operations) {
    if (operation.userId !== user.userId || operation.role !== user.profile.role) continue
    if (!(await hasCompletedDependencies(operation))) continue
    if (operation.nextAttemptAt && operation.nextAttemptAt > new Date().toISOString()) continue

    const working: OutboxOperation = {
      ...operation,
      status: 'PROCESSING',
      attempts: operation.attempts + 1,
      lastAttemptAt: new Date().toISOString(),
    }
    await updateOperation(working)
    try {
      const detail = await executeRemoteOperation(working)
      if (detail) await cacheDetail(user.userId, detail)
      await removeOperation(working.operationId)
      processed += 1
    } catch (error) {
      const kind = classifySyncError(error)
      if (kind === 'AUTH') {
        await updateOperation({ ...working, status: 'PENDING', lastError: errorMessage(error), nextAttemptAt: null })
        break
      }
      if (kind === 'TRANSIENT' && working.attempts < MAX_ATTEMPTS) {
        await updateOperation({ ...working, status: 'PENDING', lastError: errorMessage(error), nextAttemptAt: nextAttempt(working.attempts) })
        continue
      }
      await updateOperation({ ...working, status: 'NEEDS_ATTENTION', lastError: errorMessage(error), nextAttemptAt: null })
    }
  }
  return summary(user.userId, processed)
}

async function summary(userId: string, processed: number): Promise<SyncResult> {
  const operations = await listOperations(userId)
  return {
    processed,
    pending: operations.filter((operation) => operation.status !== 'NEEDS_ATTENTION').length,
    needsAttention: operations.filter((operation) => operation.status === 'NEEDS_ATTENTION').length,
  }
}

function nextAttempt(attempt: number): string {
  return new Date(Date.now() + retryDelayMs(attempt)).toISOString()
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Não foi possível enviar esta alteração.'
}

export async function retryAttentionOperations(userId: string): Promise<void> {
  const operations = await listOperations(userId, ['NEEDS_ATTENTION'])
  for (const operation of operations) {
    await updateOperation({ ...operation, status: 'PENDING', attempts: 0, lastError: null, nextAttemptAt: null })
  }
}
